/**
 * src/lib/api/client.js — Shared API client with auth + retry
 * All domain modules import apiFetch from here.
 */

import { supabase } from '../supabase';
import * as Sentry from '@sentry/react';
import { logAction, logResult, logFetchStart, logFetchSuccess, logFetchFailure } from '../observability';

export const API_BASE = '/api';

// Supabase auth (getSession/refreshSession) must never pin a request forever.
// A backgrounded tab whose token expires can leave supabase-js's refresh
// deadlocked (the refresh promise never resolves and never makes a network
// call), which previously hung every apiFetch indefinitely — surfacing as a
// permanent "Loading..." / "Saving..." with no recovery. Proven 2026-05-28 via
// CDP: expired JWT + no /auth/v1/token request + no POST + free auth-lock.
// TICKET-FILES-SAVE-HANG. Tested by tests/lib/api/client-auth-timeout.test.js.
const AUTH_RESOLVE_TIMEOUT = 8000;

function withAuthTimeout(promise, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`auth-timeout:${label}`)), AUTH_RESOLVE_TIMEOUT);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// ─── Auth helper (resilient to lock failures + auto-refresh + refresh deadlock) ─
// Cold-boot herd fix (2026-07-06): every apiFetch calls getAuthHeaders, and on
// page load many fire at once (dedupScan + supplier_contacts + app reload, …).
// Each independent supabase.auth.getSession() serializes on supabase-js's
// navigator auth-lock; the herd contends, getSession blows past the 8s timeout,
// getAuthHeaders returns {} and the request goes out unauthenticated → 401
// (ProcurementView 401 storm). Fix: (1) single-flight — collapse concurrent
// callers onto ONE in-flight getSession so they never fight the lock; (2) cache
// the resolved token in memory until near expiry so subsequent calls skip the
// lock entirely. Invalidate on any auth state change so sign-out/refresh can't
// serve a stale bearer.
let _sessionCache = null;   // { token, expiresAt }
let _authInflight = null;   // in-flight _resolveAuthHeaders() promise

// Optional-chained through .auth too: some test mocks stub supabase without an
// `auth` object, and this runs at module-load, so `supabase.auth.x` would throw
// on import and break every test that transitively imports the client.
supabase.auth?.onAuthStateChange?.(() => { _sessionCache = null; _authInflight = null; });

async function _resolveAuthHeaders() {
  try {
    const { data: { session } } = await withAuthTimeout(supabase.auth.getSession(), 'getSession');
    if (session?.access_token) {
      let token = session.access_token;
      let expiresAt = session.expires_at;
      if (expiresAt && Date.now() / 1000 > expiresAt - 60) {
        const { data } = await withAuthTimeout(supabase.auth.refreshSession(), 'refreshSession');
        if (data?.session?.access_token) {
          token = data.session.access_token;
          expiresAt = data.session.expires_at;
        }
      }
      _sessionCache = { token, expiresAt };
      return { Authorization: `Bearer ${token}` };
    }
  } catch (err) {
    console.warn('[API] Auth header skipped:', err.message?.substring(0, 60));
  }
  return {};
}

export async function getAuthHeaders() {
  // Fast path: reuse the cached token while it's comfortably valid (no lock, no I/O).
  if (_sessionCache && _sessionCache.expiresAt && Date.now() / 1000 < _sessionCache.expiresAt - 60) {
    return { Authorization: `Bearer ${_sessionCache.token}` };
  }
  // Single-flight: one getSession for the whole cold-boot herd.
  if (!_authInflight) {
    _authInflight = _resolveAuthHeaders().finally(() => { _authInflight = null; });
  }
  return _authInflight;
}

// ─── Generic fetch with fallback ────────────────────────
const FETCH_TIMEOUT = 15000; // 15s max per request

// Derive a readable action name for tracing. GET actions ride the querystring
// (?action=...); POST actions ride the JSON body — read BOTH so a mutation logs
// as e.g. `data.service_order.create` instead of an opaque `data.POST` (the gap
// that left service-order writes invisible in the trace).
function _actionName(url, method, body) {
  try {
    const u = new URL(url, window.location.origin);
    const base = u.pathname.replace(/^\/api\//, '');
    let action = u.searchParams.get('action');
    if (!action && body) {
      try {
        const parsed = typeof body === 'string' ? JSON.parse(body) : body;
        if (parsed && typeof parsed.action === 'string') action = parsed.action;
      } catch { /* spine-emit-ok: body not JSON — fall through to the method label */ }
    }
    return action ? `${base}.${action}` : `${base}.${method}`;
  } catch { return `unknown.${method}`; }
}

function _recordCount(data) {
  if (Array.isArray(data?.records)) return data.records.length;
  if (Array.isArray(data?.bundles)) return data.bundles.length;
  if (Array.isArray(data?.tasks)) return data.tasks.length;
  if (Array.isArray(data?.projects)) return data.projects.length;
  if (Array.isArray(data)) return data.length;
  return null;
}

export async function apiFetch(url, options = {}, _retried = false, _correlationId = null, _traceId = null) {
  const method = options.method || 'GET';
  const actionName = _actionName(url, method, options.body);
  // Mint a trace ID once per logical call (preserved across retries).
  // x-trace-id is thread through to the server so logEvent rows are linkable.
  const traceId = _traceId || (crypto.randomUUID?.() || `${Date.now().toString(16)}-${Math.random().toString(36).slice(2, 14)}`);
  // Only log intent on first attempt (not retries) to avoid duplicate traces
  const correlationId = _retried ? _correlationId : logAction(actionName, { url, method });
  if (!_retried) {
    logFetchStart({ correlationId, action: actionName, url, method });
  }
  const start = performance.now();
  // Per-request timeout override (options.timeout in ms). Falls back to FETCH_TIMEOUT.
  const timeoutMs = typeof options.timeout === 'number' && options.timeout > 0 ? options.timeout : FETCH_TIMEOUT;

  try {
    const auth = await getAuthHeaders();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const { timeout: _discardTimeout, noRetry: _discardNoRetry, ...fetchOptions } = options;
    const res = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...auth,
        ...(options.headers || {}),
        ...(correlationId ? { 'X-Correlation-Id': correlationId } : {}),
        'x-trace-id': traceId,
      },
    });
    clearTimeout(timer);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (method !== 'GET') {
        // options.noRetry: a non-idempotent paid call (a copilot turn = LLM spend +
        // quote writes) must not run twice because the first attempt 5xx'd late.
        if (!_retried && !options.noRetry && res.status >= 500) {
          await new Promise(r => setTimeout(r, 1500));
          return apiFetch(url, options, true, correlationId, traceId);
        }
        const duration_ms = Math.round(performance.now() - start);
        logResult(actionName, 'failure', { correlationId, duration_ms, status: res.status, error: body.error || `API error ${res.status}` });
        logFetchFailure({ correlationId, action: actionName, latency_ms: duration_ms, status: res.status, error: new Error(body.error || `API error ${res.status}`), retry_count: _retried ? 1 : 0 });
        // status lets callers distinguish a HARD conflict (409 signed/locked — stop
        // retrying) from a transient failure (edit-storm class, CEO 2026-07-08).
        // code: the server's machine-readable reason (e.g. intro_not_activated vs
        // intro_error, both 503) so a caller never has to match on error text (TICKET-1379).
        return { success: false, status: res.status, error: body.error || `API error ${res.status}`, ...(body.code ? { code: body.code } : {}) };
      }
      const duration_ms = Math.round(performance.now() - start);
      logResult(actionName, 'failure', { correlationId, duration_ms, status: res.status });
      logFetchFailure({ correlationId, action: actionName, latency_ms: duration_ms, status: res.status, error: new Error(`API ${res.status}`), retry_count: _retried ? 1 : 0 });
      // Return a consistent error object for GET so callers can distinguish
      // 403/404 (explicit auth denial) from transient failures (timeout/network).
      // This enables safe cache fallback: offline/transient → cache ok, explicit denial → cache forbidden.
      // (TICKET-610: quote authz bypass)
      return { success: false, status: res.status, error: body.error || `API error ${res.status}` };
    }
    const duration_ms = Math.round(performance.now() - start);
    const contentLengthHeader = res.headers?.get?.('content-length');
    const content_length = contentLengthHeader ? parseInt(contentLengthHeader, 10) : null;
    logResult(actionName, 'success', { correlationId, duration_ms, status: res.status });
    const data = await res.json();
    logFetchSuccess({
      correlationId,
      action: actionName,
      latency_ms: duration_ms,
      record_count: _recordCount(data),
      content_length,
      status: res.status,
    });
    return data;
  } catch (err) {
    err.traceId = traceId; // thread to UI toasts so they can show the trace ID
    const duration_ms = Math.round(performance.now() - start);
    if (err.name === 'AbortError') {
      console.warn(`[API] Request timeout for ${url}`);
      logResult(actionName, 'timeout', { correlationId, duration_ms });
      logFetchFailure({ correlationId, action: actionName, latency_ms: duration_ms, status: null, error: err, retry_count: _retried ? 1 : 0 });
      return method !== 'GET'
        ? { success: false, error: 'Request timed out — please try again', traceId }
        : null;
    }
    // noRetry here too (TICKET-899): a dropped connection AFTER the server committed a
    // paid copilot turn would otherwise resubmit it -- the 5xx guard alone missed this path.
    if (!_retried && method !== 'GET' && !options.noRetry) {
      await new Promise(r => setTimeout(r, 1500));
      return apiFetch(url, options, true, correlationId, traceId);
    }
    Sentry.captureException(err, { extra: { url, method } });
    console.warn(`API fallback for ${url}:`, err.message);
    logResult(actionName, 'failure', { correlationId, duration_ms, error: err.message });
    logFetchFailure({ correlationId, action: actionName, latency_ms: duration_ms, status: null, error: err, retry_count: _retried ? 1 : 0 });
    return method !== 'GET' ? { success: false, error: err.message, traceId } : null;
  }
}

// Exposed for unit testing the action-label derivation (URL action vs POST body).
export const __test__ = { _actionName };
